import clsx from 'clsx';
import {
    AutoComplete as PrimeAutoComplete,
    AutoCompleteProps as PrimeAutoCompleteProps,
} from 'primereact/autocomplete';
import { Tooltip } from 'primereact/tooltip';
import { forwardRef, ReactNode, Ref, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { getPlaceholder } from '../../_utils/get-placeholder';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';

export type AutoCompleteProps = PrimeAutoCompleteProps &
    InputWrapperSharedProps & {
        prefixIcon?: ReactNode;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
    };

export interface NewPillComponentProps {
    hasNewValue: boolean;
}

function NewPillComponent({ hasNewValue }: NewPillComponentProps): ReactNode {
    if (!hasNewValue) {
        return null;
    }

    return (
        <>
            <Tooltip target="#new-pill" content="New value" position="top" />
            <span id="new-pill" className="autocomplete-new-value" />
        </>
    );
}

function AutoCompleteComponent(
    props: AutoCompleteProps,
    ref: Ref<PrimeAutoComplete>,
): ReactNode {
    const {
        name,
        value,
        labelClassName,
        horizontal,
        forceSelection,
        onBlur,
        wrapperClassName: ___,
        errorClassName: ____,
        prefixIcon: ________,
        placeholder: _______,
        bulkMode: _________,
        validate: __________,
        'data-testid': dataTestId,
        ...restProps
    } = props;
    const testId = dataTestId || name;
    const placeholder = getPlaceholder(props);
    const [inputHasNewValue, setInputHasNewValue] = useState<boolean>(false);
    const formContext = useFormContext();
    const isControlled = typeof value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'AutoComplete',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    const prefixIconTemplate = props.prefixIcon ? (
        <span className="p-inputgroup-addon">{props.prefixIcon}</span>
    ) : null;

    const controlValue = useWatch({
        control: formContext.control,
        name: name ?? '',
    });
    useEffect(() => {
        if (!props) {
            return;
        }

        const { name, suggestions, minLength } = props;
        if (!suggestions || !name) {
            return;
        }

        if (!controlValue) {
            setInputHasNewValue(false);
            return;
        }

        if (minLength && controlValue?.length < minLength) {
            setInputHasNewValue(false);
        } else {
            const foundOption = suggestions?.find((s) => {
                if (Object.hasOwn(s, 'value')) {
                    return s?.value === controlValue?.value;
                }

                return s === controlValue;
            });
            setInputHasNewValue(!foundOption && controlValue !== '');
        }
    }, [props.suggestions, controlValue, props]);

    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const { field: _____, prefixIcon: ______, ...wrapperProps } = props;

    if (!isRHFControlled) {
        const {
            wrapperClassName: _,
            labelClassName: ___,
            labelTooltip: ____,
            errorClassName: _____,
            validate: ______,
            'data-testid': _______,
            ...autoCompleteProps
        } = props;

        return (
            <InputWrapper {...wrapperProps}>
                <div className="autocomplete-wrapper p-inputgroup flex-1">
                    {prefixIconTemplate}
                    <PrimeAutoComplete
                        className="flex grow"
                        ref={ref}
                        {...autoCompleteProps}
                        pt={{ input: { root: { 'data-testid': testId } } }}
                        placeholder={placeholder}
                    />
                    <NewPillComponent hasNewValue={inputHasNewValue} />
                </div>
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name!}
            control={formContext!.control}
            rules={buildRhfRules({
                required: props.required,
                bulkMode: props.bulkMode,
                validate: props.validate,
                minLength: props.minLength,
            })}
            render={({ field, fieldState }) => {
                return (
                    <InputWrapper
                        fieldState={fieldState}
                        horizontal={horizontal}
                        labelClassName={labelClassName}
                        {...wrapperProps}
                        field={field}
                    >
                        <div className="autocomplete-wrapper p-inputgroup flex-1">
                            {prefixIconTemplate}
                            <PrimeAutoComplete
                                {...restProps}
                                pt={{
                                    input: { root: { 'data-testid': testId } },
                                }}
                                className={clsx(
                                    'flex grow overflow-hidden',
                                    fieldState?.invalid ? 'p-invalid' : null,
                                )}
                                value={searchTerm ?? field.value}
                                onChange={(e) => {
                                    // if forceSelection is false, we allow free text input
                                    if (!forceSelection) {
                                        field.onChange(e.value);
                                    } else {
                                        setSearchTerm(e.value);
                                    }
                                }}
                                onSelect={(e) => {
                                    setSearchTerm(undefined);
                                    field.onChange(e.value);
                                }}
                                ref={mergeRefs(ref, field.ref)}
                                placeholder={placeholder}
                                onBlur={(event) => {
                                    field.onBlur();
                                    onBlur?.(event);
                                }}
                            />
                            <NewPillComponent hasNewValue={inputHasNewValue} />
                        </div>
                    </InputWrapper>
                );
            }}
        />
    );
}

export const AutoComplete = forwardRef(AutoCompleteComponent);
